/**
 * Single owner of the body scroll state that Bootstrap modals create.
 *
 * Background: every modal in this app is `data-bs-backdrop="false"` and non-blocking, so the
 * page behind must stay scrollable. That is achieved by
 * `body.draggable-modal-open { overflow-y: auto !important }` (style.scss), which *masks*
 * Bootstrap's scroll lock rather than preventing it — `body.style.overflow` stays `hidden`
 * underneath the whole time a modal is open.
 *
 * Two consequences follow, and this module exists to handle both:
 *
 *  1. When modal #2 opens over modal #1, Bootstrap's ScrollBarHelper reads that still-active
 *     inline `overflow: hidden` and records it in `data-bs-overflow` as the "original" value
 *     to restore. Modal #1's deferred teardown then restores `hidden` instead of clearing it
 *     and fires `hidden.bs.modal`, which previously stripped the unlock class while modal #2
 *     was still shown — killing the scrollbar. Ownership of the class is therefore derived
 *     from live DOM state here instead of being toggled per modal element.
 *
 *  2. When React unmounts a shown modal (client-side navigation), Bootstrap never runs
 *     `hide()` / `_hideModal()` / `ScrollBarHelper.reset()`, so the lock is stranded on
 *     `<body>` for the rest of the session. Reconciling on route change clears it.
 *
 * Every operation is idempotent and derived from the DOM, so the result is correct no matter
 * how a modal lifecycle was interrupted.
 */

/**
 * Bootstrap components that take ownership of the body scroll lock via ScrollBarHelper.
 * While any of these is open the body state belongs to Bootstrap and must never be cleared.
 */
const SCROLL_LOCK_OWNER_SELECTOR = '.modal.show, .offcanvas.show';

/**
 * Components that additionally need the lock *relaxed* so the page behind stays scrollable.
 *
 * Deliberately narrower than the owner selector: an offcanvas is a **blocking** overlay and
 * must keep the page locked, whereas every modal in this app is non-blocking
 * (`data-bs-backdrop="false"`). Conflating the two would unlock scrolling behind an offcanvas.
 */
const SCROLL_UNLOCK_REQUIRED_SELECTOR = '.modal.show';

/** Application class that relaxes Bootstrap's scroll lock for non-blocking modals. */
const APP_SCROLL_UNLOCK_CLASS = 'draggable-modal-open';

/** Bootstrap's own open marker. */
const BOOTSTRAP_MODAL_OPEN_CLASS = 'modal-open';

/** Bookkeeping attributes ScrollBarHelper writes to `<body>` while a lock is active. */
const SCROLLBAR_BOOKKEEPING_ATTRIBUTES = ['data-bs-overflow', 'data-bs-padding-right'] as const;

/** Inline properties ScrollBarHelper writes to `<body>` while a lock is active. */
const SCROLLBAR_INLINE_PROPERTIES = ['overflow', 'padding-right'] as const;

/**
 * Number of Bootstrap components currently owning the body scroll lock.
 * This is the authoritative count — it is never mirrored into a counter, because every
 * counter in this codepath can drift when a modal lifecycle is interrupted.
 */
export const countOpenScrollLockOwners = (): number =>
  document.querySelectorAll(SCROLL_LOCK_OWNER_SELECTOR).length;

/** Number of open components that require the page behind to remain scrollable. */
const countModalsRequiringScrollUnlock = (): number =>
  document.querySelectorAll(SCROLL_UNLOCK_REQUIRED_SELECTOR).length;

/**
 * Ensure the page stays scrollable behind a non-blocking modal.
 *
 * Used on `show.bs.modal`, where reconciliation must NOT be used: Bootstrap dispatches that
 * event *before* it applies `.show` to the element, so the owner count still reads zero and a
 * reconcile would spuriously clear the state of the modal currently opening.
 */
export const applyModalScrollUnlock = (): void => {
  document.body.classList.add(APP_SCROLL_UNLOCK_CLASS);
};

/**
 * Bring `<body>` into agreement with the DOM.
 *
 * At least one owner shown -> never touch Bootstrap's lock. The unlock class is applied only
 * when a non-blocking modal is open, and removed otherwise, so an offcanvas keeps the page
 * locked as it is designed to.
 *
 * No owner shown -> remove every trace of modal-owned scroll state.
 *
 * Only safe where the owner count is accurate: `hidden.bs.modal` (the closing modal already
 * lost `.show` in `hide()`) and route changes.
 */
export const reconcileModalBodyScroll = (): void => {
  const { body } = document;

  if (countOpenScrollLockOwners() > 0) {
    if (countModalsRequiringScrollUnlock() > 0) {
      body.classList.add(APP_SCROLL_UNLOCK_CLASS);
    } else {
      body.classList.remove(APP_SCROLL_UNLOCK_CLASS);
    }
    return;
  }

  body.classList.remove(APP_SCROLL_UNLOCK_CLASS, BOOTSTRAP_MODAL_OPEN_CLASS);

  for (const property of SCROLLBAR_INLINE_PROPERTIES) {
    body.style.removeProperty(property);
  }

  for (const attribute of SCROLLBAR_BOOKKEEPING_ATTRIBUTES) {
    body.removeAttribute(attribute);
  }
};

/**
 * Register the listeners once, delegated on `document`.
 *
 * Bootstrap dispatches its modal events with `bubbles: true`, so a single capture-phase
 * listener covers every modal in the app — including modals that never call
 * `initializeModal()`.
 *
 * `show.bs.modal` only *applies* the unlock class; it cannot reconcile (see
 * `applyModalScrollUnlock`). Applying it at this point is also required for correctness: it
 * keeps the computed `padding-right` at `0px` so Bootstrap's scrollbar compensation does not
 * compound from one modal to the next.
 *
 * `hidden.bs.modal` reconciles, and by then the count is accurate.
 *
 * @returns an uninstall function.
 */
export const installModalScrollReconciler = (): (() => void) => {
  const handleModalShow = (): void => applyModalScrollUnlock();
  const handleModalHidden = (): void => reconcileModalBodyScroll();

  document.addEventListener('show.bs.modal', handleModalShow, true);
  document.addEventListener('hidden.bs.modal', handleModalHidden, true);

  return () => {
    document.removeEventListener('show.bs.modal', handleModalShow, true);
    document.removeEventListener('hidden.bs.modal', handleModalHidden, true);
  };
};
